import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navigationItems = [
    { label: language === 'fr' ? "Accueil" : "Home", href: "accueil" },
    { label: language === 'fr' ? "Services" : "Services", href: "services" },
    { label: language === 'fr' ? "Mes Références" : "My References", href: "references" },
    { label: language === 'fr' ? "Qui suis-je ?" : "About Me", href: "about" },
    { label: language === 'fr' ? "Collaboration" : "Collaboration", href: "collaboration" },
    { label: language === 'fr' ? "Contact" : "Contact", href: "contact" }
  ];

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 nav-glow ${
          isScrolled 
            ? 'bg-black/90 backdrop-blur-md border-b border-gray-800' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between md:justify-center h-16">

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navigationItems.map((item, index) => (
                <button 
                  key={index}
                  onClick={() => scrollToSection(item.href)}
                  className="nav-item text-white hover:text-[#DC1A5F] transition-colors duration-200 font-medium"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden z-[70] relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-white hover:text-[#DC1A5F]"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-black/95 backdrop-blur-md border-t border-gray-800 animate-fade-in relative z-[60]">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navigationItems.map((item, index) => (
                  <button 
                    key={index}
                    onClick={() => scrollToSection(item.href)}
                    className="block w-full text-left px-3 py-2 text-white hover:text-[#DC1A5F] transition-colors duration-200"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Language Selector - Indépendant - En haut initial, tout en haut absolu lors du scroll */}
      <div 
        className="fixed left-0 right-0 z-[50] transition-all duration-500"
        style={{
          top: isScrolled ? '0' : 'calc(4rem + 35px)', // Tout en haut (0) lors du scroll, sinon position initiale
          background: isScrolled ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className="flex items-center justify-center gap-2 py-2">
          <button
            onClick={() => setLanguage('fr')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
              language === 'fr'
                ? 'bg-[#DC1A5F] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            FR
          </button>
          <div className="relative">
            <button
              onClick={() => setLanguage('eng')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
                language === 'eng'
                  ? 'bg-[#DC1A5F] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              ENG
            </button>
            {/* Petite flèche clignotante inversée - disparaît quand ENG est sélectionné */}
            {language !== 'eng' && (
              <div 
                className="absolute -bottom-3 text-[#32D6E0] text-[10px]"
                style={{ 
                  left: 'calc(50% - 2px)',
                  animation: 'arrowBlink 1.5s ease-in-out infinite' 
                }}
              >
                ▲
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;